import React, { FC, memo, useCallback, useState } from 'react'
import { Grid, Tab, Tabs } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useDispatch, useSelector } from 'react-redux'
import { ITabList } from '../../../pages/Dashboard/Trading/TreasuryManagment/tabList'
import { GreenButton } from '../Buttons/GreenButton'
import { MemoFundAccountPopup } from '../../../pages/Dashboard/Trading/TreasuryManagment/TreasurySections/FundAccountPopup'
import { MemoRequestSettlementPopup } from '../../../pages/Dashboard/Trading/TreasuryManagment/TreasurySections/RequestSettlementPopup'
import {
  addNewWalletPopupSelector,
  confirmReceivedAmountSelector,
  requestSettlementPopupSelector,
} from '../../../store/selectors'
import { AddNewWalletPopup } from '../../../pages/Dashboard/Trading/TreasuryManagment/TreasurySections/AddNewWalletPopup'
import { FirstTimeDepositPopup } from '../../../pages/Dashboard/Trading/TreasuryManagment/TreasurySections/FirstTimeDepositPopup'
import { openRequestSettlementPopup } from '../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    paddingTop: 15,
    width: '100%',
    backGroundColor: 'transparent',
    '& .MuiBox-root': {
      padding: 0,
    },
    '& .Mui-selected': {
      opacity: 1,
      color: theme.palette.secondary.main,
    },
  },
  tabs: {
    justifyContent: 'space-between',
    borderBottom: '1px solid rgba(233,236,239, 0.38)',
    boxSizing: 'border-box',
    position: 'relative',
    '& button': {
      minWidth: 50,
      marginRight: 10,
      width: 'auto',
      padding: '16px 10px',
      fontSize: 14,
      fontWeight: 400,
      textTransform: 'none',
      lineHeight: 1.75,
      opacity: 0.7,
      color: theme.palette.primary.light,
      position: 'relative',
      '& span': {
        alignItems: 'flex-start',
      },
    },
  },
  tab: {
    '&:focus': {
      color: theme.palette.secondary.main,
    },
  },
  tabPanels: {
    width: '100%',
    backgroundColor: 'transparent',
    '& > div': {
      width: '100%',
    },
  },
  buttonsWrap: {
    '& button': {
      width: 180,
      fontWeight: 'bold',
      opacity: 1,
      marginLeft: theme.spacing(2),
      marginRight: theme.spacing(0),
    },
  },
}))

interface TabsProps {
  tabList: ITabList[]
  twoButtons?: boolean
}

const TabsComponent: FC<TabsProps> = ({ tabList, twoButtons }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [value, setValue] = useState<number>(0)
  const [isOpenFundModal, setOpenFundModal] = useState<boolean>(false)

  const isOpenAddNewWalletModal = useSelector(addNewWalletPopupSelector)
  const isOpenConfirmReceivedAmountModal = useSelector(confirmReceivedAmountSelector)
  const isOpenRequestSettlementModal = useSelector(requestSettlementPopupSelector)

  const openModal = useCallback(
    (event) => {
      if (event.target.textContent === 'Fund Account') setOpenFundModal((prevState) => !prevState)
      if (event.target.textContent === 'Request Settlement') dispatch(openRequestSettlementPopup({ open: true }))
    },
    [setOpenFundModal, dispatch],
  )

  const closeModal = useCallback(() => {
    setOpenFundModal(false)
    dispatch(openRequestSettlementPopup({ open: false }))
  }, [setOpenFundModal, dispatch])

  const handleChange = useCallback(
    (event: React.ChangeEvent<unknown>, newValue: number) => {
      setValue(newValue)
      return newValue
    },
    [setValue],
  )

  const CurrentTab = tabList[value].component

  return (
    <div className={classes.root}>
      <Grid container className={classes.tabs}>
        <Tabs value={value} onChange={handleChange} indicatorColor="secondary">
          {tabList.map((tabItem: ITabList, idx: number) => (
            <Tab label={tabItem.tabLabel} key={idx} className={classes.tab} />
          ))}
        </Tabs>

        {twoButtons && (
          <Grid className={classes.buttonsWrap}>
            <GreenButton text={'Fund Account'} width={180} onClick={openModal} />
            <GreenButton text={'Request Settlement'} width={180} onClick={openModal} />
          </Grid>
        )}
      </Grid>

      <Grid container className={classes.tabPanels}>
        <CurrentTab />
      </Grid>

      <AddNewWalletPopup isOpen={isOpenAddNewWalletModal} closePrevModal={closeModal} />
      <FirstTimeDepositPopup isOpen={isOpenConfirmReceivedAmountModal} />
      <MemoFundAccountPopup isOpen={isOpenFundModal} modalClose={closeModal} />
      <MemoRequestSettlementPopup isOpen={isOpenRequestSettlementModal} modalClose={closeModal} />
    </div>
  )
}

export const TabsComponentMemo = memo(TabsComponent)
